import * as React from "react";
import useEmblaCarousel, {
	type UseEmblaCarouselType,
} from "embla-carousel-react";
import { WheelGesturesPlugin } from "embla-carousel-wheel-gestures";
import { ArrowLeft, ArrowRight } from "untitledui-js/react";

import { cn } from "@bidbattles/bloks/lib/ui";
import { Button } from "@bidbattles/bloks/components/ui/button";

type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
	opts?: CarouselOptions;
	plugins?: CarouselPlugin;
	orientation?: "horizontal" | "vertical";
	setApi?: (api: CarouselApi) => void;
};

type CarouselContextProps = {
	carouselRef: ReturnType<typeof useEmblaCarousel>[0];
	api: ReturnType<typeof useEmblaCarousel>[1];
	scrollPrev: () => void;
	scrollNext: () => void;
	canScrollPrev: boolean;
	canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = React.createContext<CarouselContextProps | null>(null);

function useCarousel() {
	const context = React.useContext(CarouselContext);

	if (!context) {
		throw new Error("useCarousel must be used within a <Carousel />");
	}

	return context;
}

const EmblaHorizontalScrollPlugin = WheelGesturesPlugin({
	forceWheelAxis: "x",
});

function Carousel({
	orientation = "horizontal",
	opts,
	setApi,
	plugins,
	className,
	children,
	...props
}: React.ComponentProps<"section"> & CarouselProps) {
	const [carouselRef, api] = useEmblaCarousel(
		{
			...opts,
			axis: orientation === "horizontal" ? "x" : "y",
		},
		plugins,
	);
	const [canScrollPrev, setCanScrollPrev] = React.useState(false);
	const [canScrollNext, setCanScrollNext] = React.useState(false);

	const onSelect = React.useCallback((api: CarouselApi) => {
		if (!api) return;
		setCanScrollPrev(api.canScrollPrev());
		setCanScrollNext(api.canScrollNext());
	}, []);

	const scrollPrev = React.useCallback(() => {
		api?.scrollPrev();
	}, [api]);

	const scrollNext = React.useCallback(() => {
		api?.scrollNext();
	}, [api]);

	const handleKeyDown = React.useCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (event.key === "ArrowLeft") {
				event.preventDefault();
				scrollPrev();
			} else if (event.key === "ArrowRight") {
				event.preventDefault();
				scrollNext();
			}
		},
		[scrollPrev, scrollNext],
	);

	React.useEffect(() => {
		if (!api || !setApi) return;
		setApi(api);
	}, [api, setApi]);

	React.useEffect(() => {
		if (!api) return;
		onSelect(api);
		api.on("reInit", onSelect);
		api.on("select", onSelect);

		return () => {
			api?.off("select", onSelect);
		};
	}, [api, onSelect]);

	return (
		<CarouselContext.Provider
			value={{
				carouselRef,
				api: api,
				opts,
				orientation:
					orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
				scrollPrev,
				scrollNext,
				canScrollPrev,
				canScrollNext,
			}}
		>
			<section
				onKeyDownCapture={handleKeyDown}
				className={cn("relative", className)}
				aria-roledescription="carousel"
				data-slot="carousel"
				{...props}
			>
				{children}
			</section>
		</CarouselContext.Provider>
	);
}

function CarouselContent({
	className,
	containerClassName,
	render: renderProp,
	...props
}: React.ComponentProps<"div"> & {
	render?: React.ReactElement;
	containerClassName?: string;
}) {
	const { carouselRef, orientation } = useCarousel();
	const render = renderProp ?? <div />;

	return React.cloneElement(
		render,
		{
			ref: carouselRef,
			className: cn("overflow-hidden", containerClassName),
			"data-slot": "carousel-content",
		},
		<div
			className={cn(
				"flex",
				orientation === "horizontal" ? "-ms-4" : "-mt-4 flex-col",
				className,
			)}
			{...props}
		/>,
	);
}

function CarouselItem({
	className,
	flush,
	render: renderProp,
	...props
}: React.ComponentProps<"div"> & {
	flush?: boolean;
	render?: (props: React.ComponentProps<"div">) => React.ReactElement;
}) {
	const { orientation } = useCarousel();
	const defaultRender = (props: React.ComponentProps<"div">) => (
		<div {...props} />
	);
	const render = renderProp ?? defaultRender;

	return render({
		role: "group",
		"aria-roledescription": "slide",
		className: cn(
			"min-w-0 shrink-0 grow-0 basis-full",
			!flush && (orientation === "horizontal" ? "ms-4" : "mt-4"),
			className,
		),
		...props,
	});
}

function CarouselPrevious({
	className,
	label,
	variant = "outline",
	size = "icon",
	...props
}: { label: string } & React.ComponentProps<typeof Button>) {
	const { orientation, scrollPrev, canScrollPrev } = useCarousel();

	return (
		<Button
			data-slot="carousel-previous"
			variant={variant}
			size={size}
			className={cn(
				"bg-gray-warm-100 size-8 rounded-full inline-flex items-center justify-center border-0",
				orientation !== "horizontal" && "rotate-90",
				className,
			)}
			disabled={!canScrollPrev}
			onClick={scrollPrev}
			{...props}
		>
			<ArrowLeft />
			<span className="sr-only">{label}</span>
		</Button>
	);
}

function CarouselNext({
	className,
	label,
	variant = "outline",
	size = "icon",
	...props
}: { label: string } & React.ComponentProps<typeof Button>) {
	const { orientation, scrollNext, canScrollNext } = useCarousel();

	return (
		<Button
			data-slot="carousel-next"
			variant={variant}
			size={size}
			className={cn(
				"bg-gray-warm-100 size-8 rounded-full inline-flex items-center justify-center border-0",
				orientation !== "horizontal" && "rotate-90",
				className,
			)}
			disabled={!canScrollNext}
			onClick={scrollNext}
			{...props}
		>
			<ArrowRight />
			<span className="sr-only">{label}</span>
		</Button>
	);
}

type UseDotButtonType = {
	selectedIndex: number;
	scrollSnaps: number[];
	onDotButtonClick: (index: number) => void;
};

const useDotButton = (
	emblaApi: CarouselContextProps["api"],
): UseDotButtonType => {
	const [selectedIndex, setSelectedIndex] = React.useState(0);
	const [scrollSnaps, setScrollSnaps] = React.useState<number[]>([]);

	const onDotButtonClick = React.useCallback(
		(index: number) => {
			if (!emblaApi) return;
			emblaApi.scrollTo(index);
		},
		[emblaApi],
	);

	const onInit = React.useCallback((emblaApi: CarouselContextProps["api"]) => {
		setScrollSnaps(emblaApi?.scrollSnapList() ?? []);
	}, []);

	const onSelect = React.useCallback(
		(emblaApi: CarouselContextProps["api"]) => {
			setSelectedIndex(emblaApi?.selectedScrollSnap() ?? 0);
		},
		[],
	);

	React.useEffect(() => {
		if (!emblaApi) return;

		onInit(emblaApi);
		onSelect(emblaApi);
		emblaApi.on("reInit", onInit).on("reInit", onSelect).on("select", onSelect);
	}, [emblaApi, onInit, onSelect]);

	return {
		selectedIndex,
		scrollSnaps,
		onDotButtonClick,
	};
};

function DotButton(props: React.ComponentProps<"button">) {
	const { children, ...restProps } = props;

	return (
		<button type="button" {...restProps}>
			{children}
		</button>
	);
}

function CarouselIndicators({
	className,
	...props
}: React.ComponentProps<"div">) {
	const { api } = useCarousel();
	const { scrollSnaps, selectedIndex, onDotButtonClick } = useDotButton(api);
	return (
		<div className={cn("flex gap-3", className)} {...props}>
			{scrollSnaps.map((snap, index) => (
				<DotButton
					key={`dot-${snap}`}
					onClick={() => onDotButtonClick(index)}
					className={cn(
						"size-2 rounded-full shrink-0",
						index === selectedIndex ? "bg-white" : "bg-gray-400",
					)}
				/>
			))}
		</div>
	);
}
export {
	type CarouselApi,
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselPrevious,
	CarouselNext,
	CarouselIndicators,
	EmblaHorizontalScrollPlugin,
};
